import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getFormTemplate,
  getFormTemplateVersions,
  submitForm,
} from "../../api/formTemplates";

export default function MonteurFormRunnerPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [version, setVersion] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError("");
        const [templateData, versions] = await Promise.all([
          getFormTemplate(templateId),
          getFormTemplateVersions(templateId),
        ]);

        const published = versions.filter((item) => String(item?.status || "") === "published");
        const current = published.find((item) => Number(item?.version) === Number(templateData?.currentVersion));
        const selectedVersion = current || published[0] || null;

        setTemplate(templateData || null);
        setVersion(selectedVersion);
        setAnswers({});

        if (!selectedVersion) {
          setError("Geen gepubliceerde template versie gevonden.");
        }
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || "Form template laden mislukt");
      } finally {
        setLoading(false);
      }
    }

    if (templateId) {
      load();
    }
  }, [templateId]);

  const sections = useMemo(() => {
    if (!version?.definition?.sections || !Array.isArray(version.definition.sections)) return [];
    return version.definition.sections;
  }, [version]);

  function setAnswer(fieldId, value) {
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
  }

  async function handlePhoto(fieldId, file) {
    if (!file) return;
    const dataUrl = await readAsDataUrl(file);
    setAnswer(fieldId, dataUrl);
  }

  async function handleSubmit() {
    if (!template?._id || !version?._id) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await submitForm({
        templateId: template._id,
        templateVersionId: version._id,
        answers,
        status: "submitted",
      });

      setSuccess("Formulier ingediend");
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Indienen mislukt");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Form Runner</h2>
        <p className="mt-1 text-sm text-gray-600">
          {template?.name || "Template"} {version ? `(v${version.version})` : ""}
        </p>
      </section>

      <section className="rounded-xl border bg-white p-5 shadow-sm">
        {loading && <p className="text-sm text-gray-500">Laden...</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {success && <p className="text-sm text-emerald-700">{success}</p>}

        {!loading && !error && sections.length === 0 && (
          <p className="text-sm text-gray-500">Template bevat nog geen secties.</p>
        )}

        <div className="space-y-4">
          {sections.map((section, sectionIndex) => (
            <div key={section.id || sectionIndex} className="rounded-lg border p-4">
              <h3 className="text-sm font-semibold text-gray-900">{section.title || `Sectie ${sectionIndex + 1}`}</h3>

              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {(Array.isArray(section.fields) ? section.fields : []).map((field, fieldIndex) => {
                  const fieldId = String(field?.id || `f_${sectionIndex}_${fieldIndex}`);
                  const fieldType = String(field?.type || "text").toLowerCase();
                  const value = answers[fieldId];

                  if (fieldType === "sectiontitle") {
                    return (
                      <div key={fieldId} className="md:col-span-2 rounded bg-gray-50 px-3 py-2">
                        <p className="text-sm font-semibold text-gray-800">{field?.label || "Sectietitel"}</p>
                        {field?.helpText && <p className="mt-1 text-xs text-gray-600">{field.helpText}</p>}
                      </div>
                    );
                  }

                  return (
                    <label key={fieldId} className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">
                        {field?.label || fieldId}
                        {field?.required ? " *" : ""}
                      </span>
                      {field?.helpText && <span className="mb-1 block text-xs text-gray-500">{field.helpText}</span>}

                      {fieldType === "textarea" && (
                        <textarea
                          value={String(value || "")}
                          onChange={(event) => setAnswer(fieldId, event.target.value)}
                          className="min-h-[90px] w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
                          placeholder={field?.placeholder || ""}
                        />
                      )}

                      {fieldType === "select" && (
                        <select
                          value={String(value || "")}
                          onChange={(event) => setAnswer(fieldId, event.target.value)}
                          className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
                        >
                          <option value="">-- kies --</option>
                          {(Array.isArray(field?.options) ? field.options : []).map((option) => (
                            <option key={String(option)} value={String(option)}>
                              {String(option)}
                            </option>
                          ))}
                        </select>
                      )}

                      {fieldType === "checkbox" && (
                        <input
                          type="checkbox"
                          checked={Boolean(value)}
                          onChange={(event) => setAnswer(fieldId, event.target.checked)}
                          className="h-4 w-4"
                        />
                      )}

                      {fieldType === "yesno" && (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setAnswer(fieldId, "yes")}
                            className={`rounded border px-3 py-2 text-sm ${value === "yes" ? "bg-green-600 text-white" : "bg-white"}`}
                          >
                            Ja
                          </button>
                          <button
                            type="button"
                            onClick={() => setAnswer(fieldId, "no")}
                            className={`rounded border px-3 py-2 text-sm ${value === "no" ? "bg-red-600 text-white" : "bg-white"}`}
                          >
                            Nee
                          </button>
                        </div>
                      )}

                      {fieldType === "photo" && (
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(event) => handlePhoto(fieldId, event.target.files?.[0])}
                          className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
                        />
                      )}

                      {fieldType === "signature" && (
                        <input
                          value={String(value || "")}
                          onChange={(event) => setAnswer(fieldId, event.target.value)}
                          className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
                          placeholder="Naam/handtekening"
                        />
                      )}

                      {!["textarea", "select", "checkbox", "yesno", "photo", "signature"].includes(fieldType) && (
                        <input
                          type={fieldType === "number" ? "number" : fieldType === "date" ? "date" : "text"}
                          value={String(value || "")}
                          onChange={(event) => setAnswer(fieldId, event.target.value)}
                          className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
                          placeholder={field?.placeholder || ""}
                        />
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !version}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? "Indienen..." : "Indienen"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/monteur/templates")}
            className="rounded border border-gray-300 bg-white px-4 py-2 text-sm text-gray-800 hover:bg-gray-50"
          >
            Terug naar templates
          </button>
        </div>
      </section>
    </div>
  );
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Bestand lezen mislukt"));
    reader.readAsDataURL(file);
  });
}
